import type { EnumValue } from '@lucidtech/las-sdk-browser';
import { useDebounce, useLocalStorage } from '@uidotdev/usehooks';
import { memo, useCallback, useMemo, useState } from 'react';
import { Header, MenuSection, MenuTrigger } from 'react-aria-components';

import { Badge, Button, Flex, Heading, Menu, MenuItem, MenuItemProps, Popover, Search, Text } from '@/components/core';
import { ChevronIcon } from '@/components/icons';
import { FieldValueState, FormValue } from '@/store';
import { isNumber } from '@/utils';

type EnumCandidate = FieldValueState & {
  description?: string;
};

type EnumItemProps = MenuItemProps & {
  candidate: EnumCandidate;
};

const IEnumItem = ({ candidate, ...rest }: EnumItemProps) => {
  return (
    <MenuItem {...rest}>
      <Flex className="items-center justify-center rounded-sm border border-gray-200 px-1 py-0.5 shadow-xs">
        <Text size="sm">{candidate.value}</Text>
      </Flex>
      {candidate.description && candidate.value !== candidate.description ? (
        <Text size="sm" className="!font-regular !text-gray-500">
          {candidate.description}
        </Text>
      ) : null}
      {isNumber(candidate.confidence) ? (
        <>
          <div className="grow" />
          <Badge variant="success" label={`${(candidate.confidence * 100).toFixed(0)}%`} />
        </>
      ) : null}
    </MenuItem>
  );
};

const EnumItem = memo(IEnumItem) as typeof IEnumItem;

export type EnumMenuProps = {
  formValue: FormValue;
  enumValues?: EnumValue[];
  onAction: (fieldValue: FieldValueState) => void;
};

const IEnumMenu = ({ formValue, enumValues, onAction: onActionFn }: EnumMenuProps) => {
  const [recentlyUsed, setRecentlyUsed] = useLocalStorage<EnumValue[]>('cradl:recentlyUsedEnumValues', []);

  const onAction = useCallback(
    (candidate: EnumCandidate) => {
      setRecentlyUsed((prev) => {
        if (prev.some((c) => c.value === candidate.value && c.description === candidate.description)) {
          return prev;
        }
        return [...prev, { value: candidate.value, description: candidate.description }] as EnumValue[];
      });
      onActionFn(candidate);
    },
    [onActionFn, setRecentlyUsed]
  );

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const { candidates, recentlyUsedCandidates } = useMemo(() => {
    const searchFilter = (ev: EnumValue) => {
      if (debouncedSearch) {
        const valueSearch = ev.value?.search(debouncedSearch) ?? -1;
        const descriptionSearch = ev.description?.search(debouncedSearch) ?? -1;
        if (valueSearch === -1 && descriptionSearch === -1) {
          return false;
        }
      }
      return true;
    };
    const getConfidence = (value: string) => {
      return formValue.candidates?.find((c) => c.value === value)?.confidence;
    };
    const candidates = enumValues
      ?.filter(searchFilter)
      ?.map((ev) => ({ ...ev, rawValue: ev.value, confidence: getConfidence(ev.value) }))
      ?.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));

    const recentlyUsedCandidates = recentlyUsed
      .filter((c) => candidates?.some((ev) => ev.value === c.value && ev.description === c.description))
      .map((ev) => ({ ...ev, rawValue: ev.value, confidence: getConfidence(ev.value) }))
      .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));

    return { candidates, recentlyUsedCandidates };
  }, [debouncedSearch, enumValues, formValue.candidates, recentlyUsed]);

  if (!candidates) return null;

  return (
    <MenuTrigger>
      <Button excludeFromTabOrder variant="plain">
        <ChevronIcon rotation="down" />
      </Button>
      <Popover className="flex flex-col">
        <Search
          clear={() => setSearch('')}
          props={{
            input: {
              value: search,
              onChange: (e) => setSearch(e.target.value),
              placeholder: 'Search...',
            },
          }}
        />

        <Menu className="gap-sm flex flex-col overflow-auto bg-white">
          {recentlyUsedCandidates.length > 0 ? (
            <MenuSection>
              <Header className="px-2">
                <Heading size="xs" className="text-gray-500">
                  Recently used
                </Heading>
              </Header>
              {recentlyUsedCandidates.map((candidate) => {
                return (
                  <EnumItem
                    candidate={candidate}
                    onAction={() => onAction(candidate)}
                    key={JSON.stringify(candidate)}
                  />
                );
              })}
            </MenuSection>
          ) : null}

          <MenuSection>
            <Header className="px-2">
              <Heading size="xs" className="text-gray-500">
                All
              </Heading>
            </Header>
            {candidates.map((candidate) => {
              return (
                <EnumItem candidate={candidate} onAction={() => onAction(candidate)} key={JSON.stringify(candidate)} />
              );
            })}
          </MenuSection>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};

export const EnumMenu = memo(IEnumMenu) as typeof IEnumMenu;
